import type {
  TClockEventStatus,
  TClockType,
  TCollaboratorRow,
  TDailyDetail,
  TDayRecord,
} from '../../types';

const CLOCK_TYPES: TClockType[] = [
  'web',
  'qr_tablet',
  'app_mobile',
  'biometric',
];

const STATUSES: TClockEventStatus[] = [
  'on_time',
  'acceptable',
  'late_or_absent',
];

function generateDayRecords(dateStr: string, entryHour: number): TDayRecord[] {
  const pad = (n: number) => String(n).padStart(2, '0');
  const entryVariance = Math.floor(Math.random() * 5);
  const breakStartVariance = Math.floor(Math.random() * 20);
  const breakEndVariance = Math.floor(Math.random() * 25);
  const exitVariance = Math.floor(Math.random() * 15) - 10;

  return [
    {
      id: `${dateStr}-entry`,
      actualTime: `${pad(entryHour)}:${pad(entryVariance)}`,
      estimatedTime: `${pad(entryHour)}:00`,
      eventType: 'entry',
      status: entryVariance <= 2 ? 'on_time' : 'acceptable',
      location: 'Oficina Central',
      clockType: CLOCK_TYPES[Math.floor(Math.random() * CLOCK_TYPES.length)],
    },
    {
      id: `${dateStr}-break-start`,
      actualTime: `${pad(entryHour + 4)}:${pad(breakStartVariance)}`,
      estimatedTime: `${pad(entryHour + 4)}:00`,
      eventType: 'break_start',
      status: STATUSES[Math.floor(Math.random() * STATUSES.length)],
      location: 'Oficina Central',
      clockType: CLOCK_TYPES[Math.floor(Math.random() * CLOCK_TYPES.length)],
    },
    {
      id: `${dateStr}-break-end`,
      actualTime: `${pad(entryHour + 5)}:${pad(breakEndVariance)}`,
      estimatedTime: `${pad(entryHour + 5)}:00`,
      eventType: 'break_end',
      status:
        breakEndVariance > 15
          ? 'late_or_absent'
          : breakEndVariance > 5
            ? 'acceptable'
            : 'on_time',
      location: 'Oficina Central',
      clockType: CLOCK_TYPES[Math.floor(Math.random() * CLOCK_TYPES.length)],
    },
    {
      id: `${dateStr}-exit`,
      actualTime: `${pad(entryHour + 8)}:${pad(Math.max(0, 50 + exitVariance))}`,
      estimatedTime: `${pad(entryHour + 9)}:00`,
      eventType: 'exit',
      status: exitVariance >= 0 ? 'on_time' : 'acceptable',
      location: 'Oficina Central',
      clockType: CLOCK_TYPES[Math.floor(Math.random() * CLOCK_TYPES.length)],
    },
  ];
}

function generateDailyDetails(
  startDate: string,
  endDate: string,
  plannedHoursPerDay: number,
): TDailyDetail[] {
  const details: TDailyDetail[] = [];
  const start = new Date(startDate);
  const end = new Date(endDate);
  let id = 1;

  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const dayOfWeek = d.getDay();

    if (dayOfWeek === 0 || dayOfWeek === 6) {
      continue;
    }

    const workedMinutes =
      plannedHoursPerDay * 60 + Math.floor(Math.random() * 90) - 15;
    const workedHours = Math.floor(workedMinutes / 60);
    const workedMins = workedMinutes % 60;

    const overtimeMinutes = Math.max(
      0,
      workedMinutes - plannedHoursPerDay * 60,
    );
    const lateMinutes =
      Math.random() > 0.85 ? Math.floor(Math.random() * 20) + 5 : 0;

    const dateStr = d.toISOString().split('T')[0];

    details.push({
      id: `d-${id++}`,
      date: dateStr,
      plannedHoursFormatted: `${plannedHoursPerDay}:00 h`,
      workedHoursFormatted: `${workedHours}:${String(workedMins).padStart(2, '0')} h`,
      overtimeFormatted:
        overtimeMinutes > 0
          ? `+${Math.floor(overtimeMinutes / 60)}:${String(overtimeMinutes % 60).padStart(2, '0')} h`
          : '0:00 h',
      lateHoursFormatted:
        lateMinutes > 0
          ? `-${Math.floor(lateMinutes / 60)}:${String(lateMinutes % 60).padStart(2, '0')} h`
          : '0:00 h',
      records: generateDayRecords(dateStr, 8),
    });
  }

  return details;
}

const SHIFT_MANANA_1 = { name: 'Turno Mañana 1', color: '#4B22F4' };
const SHIFT_MANANA_2 = { name: 'Turno Mañana 2', color: '#00B4D8' };
const SHIFT_ROT_OPERATIVO = { name: 'Rot. Operativo', color: '#EF476F' };

type TMockCollaboratorInput = {
  id: string;
  name: string;
  email: string;
  role: string;
  shift: { name: string; color: string };
  plannedHoursPerDay: number;
  scheduledTotalFormatted: string;
  workedTotalFormatted: string;
  workedPercentage: number;
  overtimeFormatted: string;
  lateHoursFormatted: string;
};

const COLLABORATORS_INPUT: TMockCollaboratorInput[] = [
  {
    id: '1',
    name: 'Ana Garcia',
    email: 'ana.garcia@empresa.com',
    role: 'Analista de RRHH',
    shift: SHIFT_MANANA_1,
    plannedHoursPerDay: 8,
    scheduledTotalFormatted: '200:00 h',
    workedTotalFormatted: '212:25 h',
    workedPercentage: 100,
    overtimeFormatted: '+12:55 h',
    lateHoursFormatted: '-0:30 h',
  },
  {
    id: '2',
    name: 'Carlos Lopez',
    email: 'carlos.lopez@empresa.com',
    role: 'Desarrollador Senior',
    shift: SHIFT_MANANA_1,
    plannedHoursPerDay: 8,
    scheduledTotalFormatted: '200:00 h',
    workedTotalFormatted: '214:06 h',
    workedPercentage: 100,
    overtimeFormatted: '+14:26 h',
    lateHoursFormatted: '-0:20 h',
  },
  {
    id: '3',
    name: 'Maria Rodriguez',
    email: 'maria.rodriguez@empresa.com',
    role: 'Diseñadora UX',
    shift: SHIFT_MANANA_1,
    plannedHoursPerDay: 8,
    scheduledTotalFormatted: '200:00 h',
    workedTotalFormatted: '215:08 h',
    workedPercentage: 100,
    overtimeFormatted: '+15:58 h',
    lateHoursFormatted: '-0:50 h',
  },
  {
    id: '4',
    name: 'Pedro Martinez',
    email: 'pedro.martinez@empresa.com',
    role: 'Contador',
    shift: SHIFT_MANANA_2,
    plannedHoursPerDay: 8,
    scheduledTotalFormatted: '217:30 h',
    workedTotalFormatted: '235:25 h',
    workedPercentage: 100,
    overtimeFormatted: '+19:05 h',
    lateHoursFormatted: '-1:10 h',
  },
  {
    id: '5',
    name: 'Laura Sanchez',
    email: 'laura.sanchez@empresa.com',
    role: 'Gerente de Operaciones',
    shift: SHIFT_MANANA_1,
    plannedHoursPerDay: 8,
    scheduledTotalFormatted: '200:00 h',
    workedTotalFormatted: '216:13 h',
    workedPercentage: 100,
    overtimeFormatted: '+16:33 h',
    lateHoursFormatted: '-0:20 h',
  },
  {
    id: '6',
    name: 'Roberto Diaz',
    email: 'roberto.diaz@empresa.com',
    role: 'Jefe de Logística',
    shift: SHIFT_ROT_OPERATIVO,
    plannedHoursPerDay: 8,
    scheduledTotalFormatted: '217:30 h',
    workedTotalFormatted: '233:30 h',
    workedPercentage: 100,
    overtimeFormatted: '+16:53 h',
    lateHoursFormatted: '-0:53 h',
  },
  {
    id: '7',
    name: 'Sofia Torres',
    email: 'sofia.torres@empresa.com',
    role: 'Asistente Administrativa',
    shift: SHIFT_MANANA_1,
    plannedHoursPerDay: 8,
    scheduledTotalFormatted: '200:00 h',
    workedTotalFormatted: '213:20 h',
    workedPercentage: 100,
    overtimeFormatted: '+14:39 h',
    lateHoursFormatted: '-1:19 h',
  },
  {
    id: '8',
    name: 'Diego Ramirez',
    email: 'diego.ramirez@empresa.com',
    role: 'Supervisor de Planta',
    shift: SHIFT_ROT_OPERATIVO,
    plannedHoursPerDay: 8,
    scheduledTotalFormatted: '217:30 h',
    workedTotalFormatted: '232:09 h',
    workedPercentage: 100,
    overtimeFormatted: '+15:59 h',
    lateHoursFormatted: '-1:20 h',
  },
  {
    id: '9',
    name: 'Valentina Flores',
    email: 'valentina.flores@empresa.com',
    role: 'Ejecutiva de Ventas',
    shift: SHIFT_MANANA_2,
    plannedHoursPerDay: 8,
    scheduledTotalFormatted: '217:30 h',
    workedTotalFormatted: '233:35 h',
    workedPercentage: 100,
    overtimeFormatted: '+16:45 h',
    lateHoursFormatted: '-0:40 h',
  },
  {
    id: '10',
    name: 'Fernando Castillo',
    email: 'fernando.castillo@empresa.com',
    role: 'Analista de Datos',
    shift: SHIFT_MANANA_1,
    plannedHoursPerDay: 8,
    scheduledTotalFormatted: '200:00 h',
    workedTotalFormatted: '214:16 h',
    workedPercentage: 100,
    overtimeFormatted: '+15:32 h',
    lateHoursFormatted: '-1:16 h',
  },
];

export const MOCK_COLLABORATORS: TCollaboratorRow[] = COLLABORATORS_INPUT.map(
  c => ({
    id: c.id,
    name: c.name,
    email: c.email,
    role: c.role,
    shiftName: c.shift.name,
    shiftColor: c.shift.color,
    workedHoursFormatted: c.workedTotalFormatted,
    scheduledHoursFormatted: c.scheduledTotalFormatted,
    workedPercentage: c.workedPercentage,
    overtimeFormatted: c.overtimeFormatted,
    lateHoursFormatted: c.lateHoursFormatted,
    dailyDetails: generateDailyDetails(
      '2026-02-02',
      '2026-03-06',
      c.plannedHoursPerDay,
    ),
  }),
);

export const MOCK_SUMMARY = {
  totalCollaborators: 10,
  totalOvertimeFormatted: '158:45 h',
  totalLateFormatted: '8:38 h',
};
